// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { connect } from 'react-redux';
import * as dashboardActions from 'admin/dashboard/actions';
import TaskItem from 'admin/dashboard/containers/items/TaskItem';
import ComputeResourceItem from 'admin/dashboard/containers/items/ComputeResourceItem';
import ServerItem from 'admin/dashboard/containers/items/ServerItem';
import UserItem from 'admin/dashboard/containers/items/UserItem';
import Ipv4Item from 'admin/dashboard/containers/items/Ipv4Item';
import { RootState } from 'admin/core/store';
import { Loader } from 'common/components';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import ClusterMemoryItem from 'admin/dashboard/containers/items/ClusterMemoryItem';
import ClusterDiskItem from 'admin/dashboard/containers/items/ClusterDiskItem';
import { useRequestCancellationEffect } from 'common/hooks/useRequestCancellationEffect';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import {
    Grid,
    GridCol,
    Translate,
} from '@plesk/ui-library';
import { SIZE } from 'common/constants';
import Survey from 'admin/common/containers/Survey/Survey';
import { track } from 'common/analytics/mixpanel';
import { ANALYTICS_EVENTS } from 'common/analytics/constants';
import FailoverDomainItem from 'admin/dashboard/containers/items/FailoverDomainItem';
import { isFeatureFlagEnabled } from 'common/modules/app/features/utils';
import { FEATURES } from 'common/modules/app/features/constants';
import { PageHeader } from 'admin/common/components/PageHeader/PageHeader';

export type DashboardProps =
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const Dashboard: React.FC<DashboardProps> = ({
    isLoadingDashboard,
    isHaEnabled,
    getDashboardData,
}) => {

    React.useEffect(() => {
        track(ANALYTICS_EVENTS.ADMIN.DASHBOARD.VISIT);
    }, []);

    useRequestCancellationEffect(token => getDashboardData(token), []);

    return (
        <>
            <PageHeader
                isButtonShown={false}
                title={<Translate content="dashboard.header" />}
            />

            <Loader isLoading={isLoadingDashboard}>
                <Grid
                    gap={SIZE.XS}
                    style={{
                        marginBottom: 16,
                        alignItems: 'stretch',
                    }}
                >
                    <GridCol lg={isHaEnabled ? 3 : 4}>
                        <ComputeResourceItem />
                    </GridCol>
                    <GridCol lg={isHaEnabled ? 2 : 4}>
                        <ServerItem />
                    </GridCol>
                    {isHaEnabled && (
                        <GridCol lg={3}>
                            <FailoverDomainItem />
                        </GridCol>
                    )}
                    <GridCol lg={4}>
                        <Grid gap={SIZE.XS} style={{ alignItems: 'stretch' }}>
                            <GridCol lg={6}>
                                <ClusterMemoryItem />
                            </GridCol>
                            <GridCol lg={6}>
                                <ClusterDiskItem />
                            </GridCol>
                            <GridCol lg={6}>
                                <Ipv4Item />
                            </GridCol>
                            <GridCol lg={6}>
                                <UserItem />
                            </GridCol>
                        </Grid>
                    </GridCol>
                </Grid>
                <Grid>
                    <GridCol lg={12}>
                        <TaskItem />
                    </GridCol>
                </Grid>
            </Loader>

            <Survey />
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    isLoadingDashboard: state.app.loadingFlags.has(LOADING_FLAGS.DASHBOARD_DATA),
    isHaEnabled: isFeatureFlagEnabled(state, FEATURES.HIGH_AVAILABILITY_CR), // TODO: SVM2-6974 remove when HA flag is retired
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    getDashboardData: bindActionCreators(dashboardActions.getDashboardData, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
